import React, { useEffect, useMemo, useState } from 'react';

import { FieldSelect } from '@/components/looker/field-select';
import type { Domain } from '@/system-logs/entities/domain';
import {
  type Filter as ServerFilter,
  type Filters,
} from '@/system-logs/entities/filters';

import type { AllIdFilters } from '../../state/selected-filters';

export type ServerLevelFiltersProps = {
  domain: Domain | undefined;
  server: Filters;
  filters: AllIdFilters;
  onChange: (filters: Partial<AllIdFilters>) => void;
  isLoading?: boolean;
};

type LevelKey = string;

type FilterWithKey = {
  key: LevelKey;
  filter: ServerFilter;
};

function clearDeeperLevels(
  changedLevel: LevelKey,
  levelsOrder: LevelKey[],
): Partial<AllIdFilters> {
  const changedIndex = levelsOrder.indexOf(changedLevel);
  const clearingUpdates: Partial<AllIdFilters> = {};
  for (let i = changedIndex + 1; i < levelsOrder.length; i++) {
    const levelToClose = levelsOrder[i];
    const filterKey = `${levelToClose}Id` as keyof AllIdFilters;
    clearingUpdates[filterKey] = undefined;
  }
  return clearingUpdates;
}

function ServerLevelFilterItem({
  level,
  serverFilter,
  filters,
  onPersist,
  isLoading = false,
}: {
  level: LevelKey;
  serverFilter: ServerFilter;
  filters: AllIdFilters;
  onPersist: (level: LevelKey, selected: string[] | undefined) => void;
  isLoading?: boolean;
}) {
  const options = serverFilter.values.map((v) => ({
    value: v.id,
    label: v.label,
  }));
  const filterKey = `${level}Id` as keyof AllIdFilters;
  const currentValue = filters[filterKey];

  const [selectedValue, setSelectedValue] = useState<string[] | undefined>(
    Array.isArray(currentValue)
      ? (currentValue as string[])
      : currentValue
        ? [currentValue as string]
        : undefined,
  );

  useEffect(() => {
    if (currentValue === undefined) setSelectedValue(undefined);
  }, [currentValue]);

  return (
    <FieldSelect
      key={level}
      className="max-w-[250px]"
      label={serverFilter.label}
      placeholder={`Filter by ${serverFilter.label}`}
      value={selectedValue?.[0]}
      options={options}
      onChange={(vals) => {
        if (!vals) {
          onPersist(level, undefined);
        }
        setSelectedValue(vals ? [vals] : undefined);
      }}
      onClose={() => onPersist(level, selectedValue)}
      isLoading={isLoading}
      disabled={isLoading}
      isClearable={true}
      isFilterable={true}
    />
  );
}

export const ServerLevelFilters: React.FC<ServerLevelFiltersProps> = ({
  domain,
  server,
  filters,
  onChange,
  isLoading = false,
}) => {
  const filtersWithKeys: FilterWithKey[] = useMemo(() => {
    if (!domain) return [];

    const domainFilters =
      domain === 'battery' ? server.batteryFilters : server.pcsFilters;

    const entries = Object.entries(domainFilters)
      .filter(([, filter]) => filter !== undefined)
      .map(([key, filter]) => ({
        key,
        filter: filter as ServerFilter,
        order: (filter as ServerFilter).order ?? 999,
      }))
      .sort((a, b) => a.order - b.order);

    return entries;
  }, [domain, server]);

  /**
   * Smoothly transitions between the placeholder and the filters using Tailwind's
   * opacity and pointer-events utilities. This avoids layout shift and provides
   * a visually appealing fade.
   */
  const showPlaceholder = !domain;
  const showFilters = domain && filtersWithKeys.length > 0;

  const levelsOrder = filtersWithKeys.map((f) => f.key);

  const handlePersist = (level: LevelKey, selected: string[] | undefined) => {
    const filterKey = `${level}Id` as keyof AllIdFilters;
    const partial: Partial<AllIdFilters> = {
      [filterKey]: selected && selected.length > 0 ? selected : undefined,
    };

    const currentValue = filters[filterKey];
    const wasCurrentlySingle =
      currentValue &&
      (!Array.isArray(currentValue) || currentValue.length === 1);
    const isBecomingMulti =
      selected && Array.isArray(selected) && selected.length >= 2;

    if (wasCurrentlySingle && isBecomingMulti) {
      const clearingUpdates = clearDeeperLevels(level, levelsOrder);
      onChange({ ...partial, ...clearingUpdates });
      return;
    }
    onChange(partial);
  };

  return (
    <div
      className="relative min-h-8"
      data-testid="system-logs-server-level-filters"
    >
      {/* Placeholder */}
      <div
        className={`absolute inset-0 flex items-center transition-opacity duration-300 ${
          showPlaceholder
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!showPlaceholder}
      >
        <div className="w-full p-4 text-sm italic text-gray-400">
          Select a domain to see available filters
        </div>
      </div>
      {/* Filters */}
      <div
        className={`flex flex-row gap-4 transition-opacity duration-300 ${
          showFilters
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!showFilters}
      >
        <div className="flex gap-2">
          {filtersWithKeys.map(({ key, filter }) => (
            <ServerLevelFilterItem
              key={key}
              level={key}
              serverFilter={filter}
              filters={filters}
              onPersist={handlePersist}
              isLoading={isLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
